import * as BABYLON from '@babylonjs/core';
import * as Event from './event';
import * as R from './renderer';
import { Type as ViewerTransformType } from './transform';
const DefaultCameraName = 'defaultCamera';
const onUserInteraction = (camera, fn) => {
    // absolutely horrifying camera input tracking event after 1s of no interaction
    let timer;
    let cameraLoaded = false;
    const debounceUserInteraction = () => {
        clearTimeout(timer);
        timer = setTimeout(() => {
            // R.emit(renderer, Event.Kind.OnSceneUserInteraction);
            fn();
        }, 1000);
    };
    camera.onViewMatrixChangedObservable.add(_ => {
        cameraLoaded && debounceUserInteraction();
        cameraLoaded = true;
    });
};
const cameraInteractionHint = (r, options, onDismiss) => {
    if (options.interactionHints.camera.active) {
        const gui = R.createGuiLayer(r);
        const hint = R.createCameraInteractionHint(r, gui, options.interactionHints.camera.image);
        const interval = setInterval(() => hint.play(), 5000);
        const c = R.canvas(r);
        const dismiss = () => {
            clearInterval(interval);
            hint.cancel();
            onDismiss();
            c?.removeEventListener('pointerdown', dismiss);
        };
        c?.addEventListener('pointerdown', dismiss);
    }
    return r;
};
export const snapshotCameraName = 'snapshotCamera';
export const createSnapshotCamera = (renderer) => {
    const startAlpha = 1.57;
    const startBeta = 1.25;
    const startRadius = 1.45;
    const startPosition = new BABYLON.Vector3(0, 0.5, 0);
    const snapshotCamera = new BABYLON.ArcRotateCamera(snapshotCameraName, startAlpha, startBeta, startRadius, startPosition, R.scene(renderer), true);
    snapshotCamera.fov = 0.4;
    return renderer;
};
/**
 * TODO: Break down into smaller chunks, create a better interface for creating and manipulating Cameras
 *
 * This is more like *createSceneCamera*, as it doesn't just create an ArcRotate camera
 */
export const createArcRotateCamera = (renderer, options) => {
    const startAlpha = Math.PI / 4;
    const startBeta = Math.PI / 2.6;
    const startRadius = 2;
    const startPosition = new BABYLON.Vector3(0, 0.5, 0);
    const scene = R.scene(renderer);
    const canvas = R.canvas(renderer);
    const camera = new BABYLON.ArcRotateCamera(DefaultCameraName, startAlpha, startBeta, startRadius, startPosition, scene, true);
    camera.attachControl(canvas, false);
    // Set some basic camera settings
    camera.minZ = 0.005; // clip at 0.5 meter
    camera.panningSensibility = 500; // how fast do you pan, set to 0 if you don't want to allow panning
    camera.panningInertia = 0;
    camera.panningOriginTarget = BABYLON.Vector3.Zero(); // where does the panning distance limit originate from
    camera.panningDistanceLimit = 1.5; // how far can you pan from the origin
    camera.wheelPrecision = 250;
    camera.allowUpsideDown = false; // don't allow zooming inverted
    camera.lowerBetaLimit = 0.5; // how high can you move the camera
    camera.upperBetaLimit = 1.6; // how low down can you move the camera
    camera.inertia = 0.8;
    camera.pinchPrecision = 300; // set pinch zoom speed to something reasonable for the scene
    let cameraInteractionHintRunning = false;
    // TODO: test this bit well
    if (options.interactionHints.camera) {
        cameraInteractionHintRunning = true;
        cameraInteractionHint(renderer, options, () => {
            cameraInteractionHintRunning = false;
        });
    }
    onUserInteraction(camera, () => {
        if (!cameraInteractionHintRunning) {
            R.emit(renderer, Event.Kind.OnSceneUserInteraction);
        }
    });
    camera.fov = BABYLON.Angle.FromDegrees(50).radians();
    // if panToModelOnSelection is true, set up panning animation on mesh selection with revert to default on non selection
    let focusAnimation = null;
    options.camera.panToModelOnSelection &&
        scene.onPointerObservable.add(pointerInfo => {
            // cancel on pan action
            if (pointerInfo.type === BABYLON.PointerEventTypes.POINTERDOWN &&
                pointerInfo.event.button == 2) {
                focusAnimation?.stop();
            }
            if (pointerInfo.type === BABYLON.PointerEventTypes.POINTERTAP) {
                const pickingInfo = scene.pick(scene.pointerX, scene.pointerY, pickedMesh => pickedMesh.isPickable && !['ground', 'wall'].includes(pickedMesh.name));
                const pickedMesh = pickingInfo?.pickedMesh;
                if (pickedMesh) {
                    const boundingSphere = pickedMesh.getBoundingInfo().boundingSphere;
                    focusAnimation = moveCameraTargetTo(scene, camera, boundingSphere.centerWorld, boundingSphere, 50);
                }
                else {
                    const rootTransform = scene.getTransformNodeByName(ViewerTransformType.ROOT_FURNITURE_TRANSFORM);
                    if (rootTransform) {
                        const boundingBox = rootTransform.getHierarchyBoundingVectors();
                        const boundingSphere = new BABYLON.BoundingSphere(boundingBox.min, boundingBox.max);
                        focusAnimation = moveCameraTargetTo(scene, camera, startPosition, boundingSphere, 50);
                    }
                }
            }
        });
    return renderer;
};
function moveCameraTargetTo(scene, camera, targetPosition, fitToboundingSphere, speed) {
    // if the screen is taller than it is wide (aspect ratio < 1), constrain it to the width by multiplying by the ratio
    const aspectRatio = scene.getEngine().getAspectRatio(camera);
    const aspectMultiplier = Math.min(1, aspectRatio);
    // get the distance in meters required to fit the bounding sphere (with a + 50% padding border)
    const targetCameraDistance = (fitToboundingSphere.radius * 1.5) / (Math.tan(camera.fov / 2) * aspectMultiplier);
    // build pan and zoom animations
    const ease = new BABYLON.CubicEase();
    ease.setEasingMode(BABYLON.EasingFunction.EASINGMODE_EASEINOUT);
    const panAnimation = new BABYLON.Animation('panCameraAnimation', 'target', speed, BABYLON.Animation.ANIMATIONTYPE_VECTOR3, BABYLON.Animation.ANIMATIONLOOPMODE_CONSTANT);
    panAnimation.setKeys([
        { frame: 0, value: camera.target },
        { frame: 60, value: targetPosition },
    ]);
    panAnimation.setEasingFunction(ease);
    const zoomAnimation = new BABYLON.Animation('zoomCameraAnimation', 'radius', speed, BABYLON.Animation.ANIMATIONTYPE_FLOAT, BABYLON.Animation.ANIMATIONLOOPMODE_CONSTANT);
    zoomAnimation.setKeys([
        { frame: 0, value: camera.radius },
        { frame: 60, value: targetCameraDistance },
    ]);
    zoomAnimation.setEasingFunction(ease);
    // group, play and return both animations
    const group = new BABYLON.AnimationGroup('focusCameraAnimationGroup', scene);
    group.addTargetedAnimation(panAnimation, camera);
    group.addTargetedAnimation(zoomAnimation, camera);
    return group.play();
}
